import React, { useState, useEffect, useCallback } from 'react';
// Make sure these import paths are correct
import { fetchOrders, deleteOrder } from '../../api/api';
import DataTable from '../DataTable/DataTable';
import Pagination from '../Pagination/Pagination';
import Modal from '../Modal/Modal';
import Spinner from '../../assets/spinner.svg';

// Import the new CSS file
import './OrdersPage.css';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchOrders(currentPage);
      setOrders(data.results);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError('Failed to fetch orders. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleEdit = (order) => {
    // For styling, we'll give the action buttons a class.
    // This logic doesn't change, but the button itself in DataTable will have the class.
    alert(`Editing is not implemented yet. Edit Order ID: ${order._id}`);
  };

  const handleDeleteRequest = (id) => {
    setItemToDelete(id);
    setIsModalOpen(true);
  };
  
  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteOrder(itemToDelete);
        setIsModalOpen(false);
        setItemToDelete(null);
        loadOrders(); 
      } catch (err) {
        alert('Failed to delete order.');
      }
    }
  };

  // The columns now have classNames for the action buttons passed down to DataTable
  const columns = [
    { header: 'Name', accessor: 'firstName' },
    { header: 'Contact', accessor: 'contactNumber' },
    { header: 'Email', accessor: 'email' },
    { header: 'Plan', accessor: 'planName' },
    { header: 'Amount', accessor: 'totalAmount' },
    { header: 'Date', accessor: 'orderDate', isDate: true },
  ];

  // Note: Your DataTable component needs to be able to accept these
  // classNames and apply them to its internal elements.
  const actionButtonClasses = {
    edit: 'nfc-action-btn nfc-edit-btn',
    delete: 'nfc-action-btn nfc-delete-btn',
    wrapper: 'nfc-action-buttons'
  };

  if (loading) {
    return (
      <div className="nfc-loading-spinner">
        <img src={Spinner} alt="Loading..." />
      </div>
    );
  }

  if (error) {
    return <div className="nfc-no-data-message">{error}</div>;
  }

  return (
    <>
      <h1 className="nfc-page-header">Bookings / Orders</h1>
      {orders.length > 0 ? (
        <>
          <div className="nfc-data-table-wrapper">
            <DataTable 
              data={orders} 
              columns={columns} 
              onEdit={handleEdit}
              onDelete={handleDeleteRequest}
              // Pass the CSS classes to the DataTable
              tableClassName="nfc-data-table"
              actionButtonClasses={actionButtonClasses}
            />
          </div>
          <Pagination 
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            // Pass the CSS classes to the Pagination component
            containerClassName="nfc-pagination"
            buttonClassName="nfc-page-btn"
            activeClassName="nfc-active"
          />
        </>
      ) : (
        <div className="nfc-no-data-message">No orders found.</div>
      )}
      
      {/* The Modal now uses the new classes for styling */}
      <Modal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        // Pass all the necessary classes to your Modal component
        overlayClassName="nfc-modal-overlay"
        dialogClassName="nfc-modal-dialog"
        headerClassName="nfc-modal-header"
        titleClassName="nfc-modal-title"
        contentClassName="nfc-modal-content"
        footerClassName="nfc-modal-footer"
        confirmBtnClassName="nfc-modal-btn nfc-modal-confirm-btn"
        cancelBtnClassName="nfc-modal-btn nfc-modal-cancel-btn"
      >
        <p>Are you sure you want to delete this item? This action cannot be undone.</p>
      </Modal>
    </>
  );
};

export default OrdersPage;